import z from 'zod';
import type { Rome } from './rome';

export enum JobSource {
  CIBLI = 'Cibli',
  FRANCE_TRAVAIL = 'FranceTravail',
  // Retained for legacy data and code paths, but not enabled for booth sources.
  CIBLI_LEGACY = 'CibliLegacy',
}

export const ACTIVE_BOOTH_JOB_SOURCES = [JobSource.CIBLI, JobSource.FRANCE_TRAVAIL] as const;

export const NO_BOOTH_JOB_SOURCES = '__NONE__';
export const BOOTH_NO_JOB_SOURCES = NO_BOOTH_JOB_SOURCES;

export function getBoothJobSources(
  configuredSources: readonly string[] | null | undefined,
): JobSource[] {
  if (!configuredSources?.length) return [...ACTIVE_BOOTH_JOB_SOURCES];
  if (configuredSources.includes(NO_BOOTH_JOB_SOURCES)) return [];
  return ACTIVE_BOOTH_JOB_SOURCES.filter(source => configuredSources.includes(source));
}

export enum MatchLevel {
  STRONG = 'strong',
  MODERATE = 'moderate',
  FALLBACK = 'fallback',
}

export enum MatchSource {
  DISTANCE = 'distance',
  SEMANTIC = 'semantic',
}

/**
 * Informations de contact pour les offres d'emploi
 * Utilisé pour les offres France Travail afin d'envoyer les infos de contact à l'utilisateur
 */
export type JobContactInfo = {
  /** Nom du contact (si disponible) */
  nom?: string;
  /** Email du contact ou lien vers l'offre (selon la doc FT, courriel contient le lien) */
  courriel?: string;
  /** URL de postulation directe (si disponible) */
  urlPostulation?: string;
  /** URL du recruteur (si disponible) */
  urlRecruteur?: string;
  /** Téléphone du contact (si disponible) */
  telephone?: string;
  /** Commentaire/instructions de contact (si disponible) */
  commentaire?: string;
};

/**
 * Complément de salaire (avantages, primes, etc.)
 */
export type SalaryComplement = {
  /** Code du complément */
  code: string;
  /** Libellé du complément */
  libelle: string;
};

/**
 * Informations détaillées sur le salaire
 * Structure alignée avec France Travail pour préserver toutes les données
 */
export type SalaryInfo = {
  /** Libellé complet du salaire (ex: "Mensuel de 2112.0 Euros à 2238.0 Euros sur 12.0 mois") */
  libelle?: string | null;
  /** Commentaire sur le salaire (si disponible) */
  commentaire?: string | null;
  /** Premier complément de salaire (ex: "Intéressement / participation") */
  complement1?: string | null;
  /** Deuxième complément de salaire (ex: "Restauration") */
  complement2?: string | null;
  /** Liste complète des compléments de salaire */
  listeComplements?: SalaryComplement[] | null;
  /** Montant du salaire extrait du libellé (pour compatibilité et recherche) */
  amount?: number | null;
  /** Périodicité du salaire (annual, monthly, hourly, daily) */
  periodicity?: SalaryPeriodicity | null;
};

// Job type based on createJob parameters
export type Job = {
  id: string;
  city: string;
  /** Coordonnées géographiques [longitude, latitude] */
  location: [number, number];
  contractType?: string;
  employmentTypes?: string[];
  status?: 'PUBLISHED' | 'DRAFT' | 'ARCHIVED' | 'PAUSED';
  /** Informations détaillées sur le salaire (structure enrichie alignée avec FT) */
  salary?: SalaryInfo | null;
  /** @deprecated Utiliser salary.amount à la place */
  salaryAmount?: number | null;
  /** @deprecated Utiliser salary.periodicity à la place */
  salaryPeriodicity?: string | null;
  experience?: number | null;
  logoUrl?: string | null;
  companyName?: string | null;
  title?: string | null;
  description?: string | null;
  expectedStartDate?: string | null;
  updatedAt?: string | null;
  jobSource: JobSource;
  /** ID de l'organisation ATS (Cibli ATS v2 uniquement) */
  organizationId?: string | null;
  /** Score total calculé par le pipeline de tri (0-21 selon les poids actuels) */
  score?: number;
  /** Niveau de correspondance dérivé du score */
  matchLevel?: MatchLevel;
  /** Source dominante du match fort (distance vs sémantique) */
  /** Indique si l'offre correspond au métier recherché (score sémantique / ROME >= seuil) */
  matchesJob?: boolean;
  /** Indique si l'offre se situe dans le rayon de déplacement souhaité par le candidat */
  matchesDistance?: boolean;
  /** Distance calculée en kilomètres entre le candidat et le lieu de travail */
  distanceKm?: number | null;
  /** Codes ROME pour les métiers (jobs) */
  romeJobs?: Rome[] | null;
  /** Codes ROME pour les compétences (skills) */
  romeSkills?: Rome[] | null;
  /** Informations de contact pour les offres France Travail (uniquement si jobSource === FRANCE_TRAVAIL) */
  contactInfo?: JobContactInfo;

  // Champs uniquement disponibles côté France Travail (optionnels)
  /** Code postal du lieu de travail (FT uniquement) */
  codePostal?: string | null;
  /** Code INSEE de la commune (FT uniquement) */
  codeInsee?: string | null;
  /** Formations requises ou souhaitées (FT uniquement) */
  formations?: Array<{
    codeFormation: string;
    domaineLibelle: string;
    niveauLibelle: string;
    commentaire?: string;
    exigence: 'E' | 'S'; // E = exigée, S = souhaitée
  }> | null;
  /** Langues requises ou souhaitées (FT uniquement) */
  langues?: Array<{
    libelle: string;
    exigence: 'E' | 'S'; // E = exigée, S = souhaitée
  }> | null;
  /** Permis requis ou souhaités (FT uniquement) */
  permis?: Array<{
    libelle: string;
    exigence: 'E' | 'S'; // E = exigé, S = souhaité
  }> | null;
  /** Outils bureautiques (FT uniquement) */
  outilsBureautiques?: string[] | null;
  /** Nombre de postes disponibles (FT uniquement) */
  nombrePostes?: number | null;
  /** Accessible aux travailleurs handicapés (FT uniquement) */
  accessibleTH?: boolean | null;
  /** Code de déplacement (FT uniquement) */
  deplacementCode?: string | null;
  /** Libellé de déplacement (FT uniquement) */
  deplacementLibelle?: string | null;
  /** Code de qualification (FT uniquement) */
  qualificationCode?: string | null;
  /** Libellé de qualification (FT uniquement) */
  qualificationLibelle?: string | null;
  /** Code NAF (FT uniquement) */
  codeNAF?: string | null;
  /** Secteur d'activité (FT uniquement) */
  secteurActivite?: string | null;
  /** Libellé du secteur d'activité (FT uniquement) */
  secteurActiviteLibelle?: string | null;
  /** Tranche d'effectif de l'établissement (FT uniquement) */
  trancheEffectifEtab?: string | null;
  /** Alternance disponible (FT uniquement) */
  alternance?: boolean | null;
  /** Durée de travail libellé (FT uniquement) */
  dureeTravailLibelle?: string | null;
  /** Durée de travail libellé converti (FT uniquement) */
  dureeTravailLibelleConverti?: string | null;
  /** Complément d'exercice (FT uniquement) */
  complementExercice?: string | null;
  /** Condition d'exercice (FT uniquement) */
  conditionExercice?: string | null;
  /** Offre en manque de candidats (FT uniquement) */
  offresManqueCandidats?: boolean | null;
  /** Employeur engagé pour le handicap (FT uniquement) */
  employeurHandiEngage?: boolean | null;
  /** Description de l'entreprise (FT uniquement) */
  entrepriseDescription?: string | null;
  /** URL de l'entreprise (FT uniquement) */
  entrepriseUrl?: string | null;
  /** Entreprise adaptée (FT uniquement) */
  entrepriseAdaptee?: boolean | null;
  /** Code ROME (FT uniquement) */
  romeCode?: string | null;
  /** Libellé ROME (FT uniquement) */
  romeLibelle?: string | null;
  /** Appellation libellé (FT uniquement) */
  appellationLibelle?: string | null;
  /** Informations ROME structurées (FT uniquement) */
  rome?: Rome | null;
  /** Libellé du type de contrat (FT uniquement) */
  typeContratLibelle?: string | null;
  /** Nature du contrat (FT uniquement) */
  natureContrat?: string | null;
  /** Libellé de l'expérience (FT uniquement) */
  experienceLibelle?: string | null;
  /** Commentaire sur l'expérience (FT uniquement) */
  experienceCommentaire?: string | null;
  /** Contexte de travail - horaires (FT uniquement) */
  horaires?: string[] | null;
  /** Contexte de travail - conditions d'exercice (FT uniquement) */
  conditionsExercice?: string[] | null;
  /** Origine de l'offre (FT uniquement) */
  origineOffre?: {
    origine: string; // '1' = France Travail, '2' = Partenaire
    urlOrigine?: string;
  } | null;
  /** Partenaires associés à l'offre (FT uniquement) */
  partenaires?: Array<Record<string, unknown>> | null;
};

// WARNING: Several fields (contractType, employmentTypes, salary, experience) are not yet standardized by the ATS; expect changes when ATS normalization lands.
// The jobs type will be updated when ATS normalization lands.

// --- Contract types: union of ATS v2 position enum + free strings (FT / legacy) ---

/** ATS v2 position enum values (codegen). Used as canonical filter values in UI. */
export const ATS_POSITION_VALUES = [
  'PERMANENT',
  'FIXED_TERM',
  'TEMPORARY',
  'INTERNSHIP',
  'WORK_STUDY',
  'FREELANCE',
  'FRANCHISE',
  'TRAINING_PROGRAM',
] as const;

export type AtsPosition = (typeof ATS_POSITION_VALUES)[number];

/** Filter value in UI: one or more ATS positions, or "all". */
export type ContractTypeFilter = AtsPosition | 'ALL';

/**
 * Job.contractType: known ATS position (v2) or any string (v1/FT free text).
 * Typing is AtsPosition | string so we can accept both and match robustly.
 */
export type ContractType = AtsPosition | string;

/** Legacy contract type values (v1/FT normalized). Kept for backward compat and FT/v1 matching. */
export const CONTRACT_TYPES = {
  CDI: 'cdi',
  CDD: 'cdd',
  INTERIM: 'interim',
  TEMPORARY: 'temporary',
  TELEWORKING: 'teleworking',
  PARTIAL: 'partial',
} as const;

export type LegacyContractType = (typeof CONTRACT_TYPES)[keyof typeof CONTRACT_TYPES];

// Standard employment type values
export const EMPLOYMENT_TYPES = {
  FULL_TIME: 'full-time',
  PART_TIME: 'part-time',
} as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPES)[keyof typeof EMPLOYMENT_TYPES];

// Salary periodicity standard values
export const SALARY_PERIODICITIES = {
  ANNUAL: 'annual',
  MONTHLY: 'monthly',
  HOURLY: 'hourly',
  DAILY: 'daily',
} as const;

export type SalaryPeriodicity = (typeof SALARY_PERIODICITIES)[keyof typeof SALARY_PERIODICITIES];

// Map from various API/user inputs to standard periodicity
export const SALARY_PERIODICITY_MAP: Record<string, SalaryPeriodicity> = {
  // Annual variants
  annuel: SALARY_PERIODICITIES.ANNUAL,
  annual: SALARY_PERIODICITIES.ANNUAL,
  yearly: SALARY_PERIODICITIES.ANNUAL,
  'par an': SALARY_PERIODICITIES.ANNUAL,
  '/an': SALARY_PERIODICITIES.ANNUAL,
  an: SALARY_PERIODICITIES.ANNUAL,
  year: SALARY_PERIODICITIES.ANNUAL,

  // Monthly variants
  mensuel: SALARY_PERIODICITIES.MONTHLY,
  monthly: SALARY_PERIODICITIES.MONTHLY,
  mois: SALARY_PERIODICITIES.MONTHLY,
  'par mois': SALARY_PERIODICITIES.MONTHLY,
  '/mois': SALARY_PERIODICITIES.MONTHLY,
  '/mo': SALARY_PERIODICITIES.MONTHLY,
  month: SALARY_PERIODICITIES.MONTHLY,

  // Hourly variants
  horaire: SALARY_PERIODICITIES.HOURLY,
  hourly: SALARY_PERIODICITIES.HOURLY,
  heure: SALARY_PERIODICITIES.HOURLY,
  '/h': SALARY_PERIODICITIES.HOURLY,
  hour: SALARY_PERIODICITIES.HOURLY,

  // Daily variants
  journalier: SALARY_PERIODICITIES.DAILY,
  daily: SALARY_PERIODICITIES.DAILY,
  jour: SALARY_PERIODICITIES.DAILY,
  '/jour': SALARY_PERIODICITIES.DAILY,
  day: SALARY_PERIODICITIES.DAILY,
};

/**
 * Normalize salary periodicity from various formats to standard value
 * @param periodicity Raw periodicity string from API
 * @returns Normalized periodicity or null if unknown
 */
export function normalizeSalaryPeriodicity(
  periodicity: string | null | undefined,
): SalaryPeriodicity | null {
  if (!periodicity) return null;

  const normalized = periodicity.toLowerCase().trim();
  return SALARY_PERIODICITY_MAP[normalized] || null;
}

// Map from Cibli API values to standard values
export const CIBLI_CONTRACT_TYPE_MAP: Record<string, ContractType> = {
  cdi: CONTRACT_TYPES.CDI,
  CDI: CONTRACT_TYPES.CDI,
  Cdi: CONTRACT_TYPES.CDI,
  cdd: CONTRACT_TYPES.CDD,
  CDD: CONTRACT_TYPES.CDD,
  Cdd: CONTRACT_TYPES.CDD,
  interim: CONTRACT_TYPES.INTERIM,
  INTERIM: CONTRACT_TYPES.INTERIM,
  Intérim: CONTRACT_TYPES.INTERIM,
  intérim: CONTRACT_TYPES.INTERIM,
  temporary: CONTRACT_TYPES.TEMPORARY,
  Temporary: CONTRACT_TYPES.TEMPORARY,
  TEMPORARY: CONTRACT_TYPES.TEMPORARY,
  teleworking: CONTRACT_TYPES.TELEWORKING,
  Teleworking: CONTRACT_TYPES.TELEWORKING,
  TELEWORKING: CONTRACT_TYPES.TELEWORKING,
  télétravail: CONTRACT_TYPES.TELEWORKING,
  Télétravail: CONTRACT_TYPES.TELEWORKING,
  partial: CONTRACT_TYPES.PARTIAL,
  Partial: CONTRACT_TYPES.PARTIAL,
  PARTIAL: CONTRACT_TYPES.PARTIAL,
  'temps-partiel': CONTRACT_TYPES.PARTIAL,
  'Temps-partiel': CONTRACT_TYPES.PARTIAL,
};

/**
 * ATS position → French label (display in UI).
 * PERMANENT → "CDI", FIXED_TERM → "CDD", etc.
 */
export const ATS_POSITION_TO_FR_LABEL: Record<AtsPosition, string> = {
  PERMANENT: 'CDI',
  FIXED_TERM: 'CDD',
  TEMPORARY: 'Intérim',
  INTERNSHIP: 'Stage',
  WORK_STUDY: 'Alternance',
  FREELANCE: 'Freelance',
  FRANCHISE: 'Franchise',
  TRAINING_PROGRAM: 'Formation',
};

/**
 * ATS position → normalized French variants (lowercase, no accents) for matching v1/FT.
 * When user selects e.g. PERMANENT ("CDI"), we match job.contractType against these strings.
 */
/** All values must be normalized (lowercase, no accents) for matching. */
export const ATS_POSITION_TO_FR_MATCH_VARIANTS: Record<AtsPosition, string[]> = {
  PERMANENT: ['cdi', 'permanent', 'contrat a duree indeterminee', 'duree indeterminee'],
  FIXED_TERM: ['cdd', 'contrat a duree determinee', 'duree determinee', 'fixed', 'term'],
  TEMPORARY: ['interim', 'temporary', 'mis', 'mission', 'contrat de mission'],
  INTERNSHIP: ['stage', 'internship', 'alternance'],
  WORK_STUDY: ['alternance', 'apprentissage', 'contrat de professionnalisation', 'work_study'],
  FREELANCE: ['freelance', 'independent', 'independant', 'portage'],
  FRANCHISE: ['franchise'],
  TRAINING_PROGRAM: ['formation', 'programme', 'training', 'contrat de formation'],
};

/**
 * Normalize string for contract-type matching: lowercase, trim, remove accents.
 */
export function normalizeContractTypeForMatch(value: string | null | undefined): string {
  if (!value || typeof value !== 'string') return '';
  return value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Returns true if job's contractType matches any of the selected filters.
 * - If filters include 'ALL' or filters are empty: matches.
 * - For CIBLI (v2): exact match (job.contractType is ATS position).
 * - For FRANCE_TRAVAIL / CIBLI_LEGACY: match using French variants (ATS_POSITION_TO_FR_MATCH_VARIANTS).
 */
export function contractTypeMatchesFilter(
  jobContractType: string | null | undefined,
  jobSource: JobSource,
  selectedFilters: ContractTypeFilter[],
): boolean {
  if (!selectedFilters.length || selectedFilters.includes('ALL')) return true;
  if (!jobContractType || !jobContractType.trim()) return false;

  const normalized = normalizeContractTypeForMatch(jobContractType);

  for (const filter of selectedFilters) {
    if (filter === 'ALL') return true;
    // v2: exact match (we store position as-is for Cibli)
    if (jobSource === JobSource.CIBLI) {
      if (jobContractType === filter) return true;
      continue;
    }
    // v1 / FT: match against French variants (normalized)
    const variants = ATS_POSITION_TO_FR_MATCH_VARIANTS[filter] ?? [];
    if (variants.some(v => normalized.includes(v) || v.includes(normalized))) return true;
  }
  return false;
}

/**
 * Résultat d'une candidature à une offre d'emploi
 * Permet d'adapter l'UI selon le type de candidature effectuée
 */
export enum ApplicationResult {
  /** Candidature envoyée directement (Cibli ATS) */
  APPLIED = 'APPLIED',
  /** Informations de contact envoyées par SMS (France Travail) */
  INFO_SENT_SMS = 'INFO_SENT_SMS',
  INFO_SENT_EMAIL = 'INFO_SENT_EMAIL',
}

export type ApplyJobParams = {
  job: Job;
  cvId: string;
};

// TODO: Merge JobsQuery and JobSearchParams into a single type :/
export const jobsQueryDtoSchema = z.object({
  page: z.coerce.number().int().positive().default(1).optional(),
  limit: z.coerce.number().int().positive().max(100).default(10).optional(),
  search: z.string().optional(),
  city: z.string().optional(),
  contractTypes: z.array(z.string()).optional(),
  employmentTypes: z.array(z.string()).optional(),
  onlyWithJobCard: z.boolean().optional(), // Internal flag, not exposed to frontend
  boothId: z.string().optional(), // ID de la cabine pour appliquer les exclusions
});

export type JobsQueryDto = z.infer<typeof jobsQueryDtoSchema>;

/**
 * Paramètres de recherche d'emploi simplifiés
 * Version simple et unifiée pour l'interface utilisateur
 */
export type JobSearchParams = {
  codePostal?: string;
  codeInsee?: string;
  commune?: string;
  distance?: number;
  motsCles?: string;
  boothId?: string;
  /**
   * Codes ROME pour les métiers (jobs)
   * Utilisés pour améliorer la précision de la recherche
   */
  romeJobs?: Rome[];
  /**
   * Codes ROME pour les compétences (skills)
   * Utilisés pour améliorer la précision de la recherche
   */
  romeSkills?: Rome[];
  /**
   * Filtre par source(s) d'emploi
   * Si non fourni ou vide, toutes les sources sont utilisées
   * Utilisé uniquement pour les tests depuis apps/bo
   */
  sources?: JobSource[];
  /**
   * Types de contrat à filtrer (ATS v2 positions ou 'ALL').
   * Vide ou 'ALL' = pas de filtre.
   */
  contractTypes?: ContractTypeFilter[];
  /**
   * Types d'emploi (temps plein/partiel) à filtrer
   */
  employmentTypes?: EmploymentType[];
  /**
   * ID de l'organisation ATS pour filtrer les offres par organisation
   */
  organizationId?: string;
  /** Mode configuration : retourne tous les jobs (y compris exclus) pour le BO */
  configMode?: boolean;
  /** machineUid de la cabine pour filtrer les jobs ATS admin par jobBoards */
  boothMachineUid?: string;
};

export type SourceMeta = {
  source: JobSource;
  status: 'ok' | 'error';
  resultCount: number;
  error?: string;
};

export type JobsResponse = {
  jobs: Job[];
  sourcesMeta: SourceMeta[];
};
